import api from './axios';
import {
  saveMedicineResults, searchMedicines, getMedicineCatalog, upsertMedicineCacheRow,
  saveCustomers, searchCustomers, saveSuppliers, searchSuppliers, saveCategories,
  saveSale, getSales, getSaleReturns, saveSaleReturn, savePurchase, getPurchases,
  saveExpense, getExpenses, removeExpense,
  getInventoryAll, getInventoryById, upsertInventoryRow, removeInventoryRow, applyInventoryMovement,
  addInventoryTx, getInventoryTx, syncMedicineCatalogFields,
  addLedgerPayment, getLedgerPayments, updateLedgerPayment, removeLedgerPayment,
  saveSettingsCache, getSettingsCache,
  enqueue, purgeQueueReferencing, getQueue, updateQueueItem,
  getAll as dbGetAll, get, put, remove, genLocalId,
  hasPendingQueue, getLocalStockBase, getConversionToBase, dropMedicineBatchCache,
  // These four are called in the background-refresh code of the list services
  // below but were never imported — the ReferenceError was swallowed by the
  // `.catch(() => {})` next to each call, so those caches were silently never
  // refreshed from the server (the Inventory list in particular).
  saveInventoryRows, savePurchases, saveSales, saveSaleReturns,
} from '../offline/db';
import { syncQueue } from '../offline/sync';
import {
  cachedGet, offlineAction, createOfflineService, login as offlineLogin,
} from '../offline/generic';

const offlineResponse = (data, extra = {}) => ({ data: { data, ...extra } });
const isNetworkError = (error) => !error?.response || error?.code === 'ERR_NETWORK' || error?.message === 'Network Error';
const nowISO = () => new Date().toISOString();
// یک خرید/فروش/... که فقط آفلاین ساخته شده و هنوز با سرور سنس نشده، این پیشوند
// را در _id خودش دارد. تا وقتی سنس نشده، سرور اصلاً از وجودش خبر ندارد — پس
// هر عملیات دیگری روی همین id (approve/receive/cancel/payment/update/delete)
// باید مستقیم محلی انجام شود، حتی اگر در همان لحظه اینترنت وصل باشد؛ در غیر
// این صورت سرور با خطای 404 رد می‌کند و آن عملیات اصلاً وارد صف سنس نمی‌شود.
const isOfflineId = (id) => String(id || '').startsWith('offline-');

const paginate = (rows, params = {}) => {
  const page = Math.max(1, Number(params.page || 1));
  const limit = Math.max(1, Number(params.limit || 20));
  const start = (page - 1) * limit;
  return { data: rows.slice(start, start + limit), pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) || 1 } };
};

// Fast, shallow text search used by every offline list fallback
// (purchases/sales/sale-returns/expenses/etc). Was JSON.stringify(r) on
// every row on every keystroke — serializing a whole nested record (items,
// populated supplier/customer, etc.) is slow once there's real history.
// This walks string/number values directly instead, capped at a sane depth
// so it can't run away on deeply nested data, and caches the result per row
// so re-searching doesn't redo the walk for rows already seen.
function rowSearchText(row) {
  if (row && row.__st) return row.__st;
  const walk = (val, depth) => {
    if (val == null) return '';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (depth > 3) return '';
    if (Array.isArray(val)) return val.map(v => walk(v, depth + 1)).join(' ');
    if (typeof val === 'object') return Object.values(val).map(v => walk(v, depth + 1)).join(' ');
    return '';
  };
  const st = walk(row, 0).toLowerCase();
  if (row && typeof row === 'object') {
    try { Object.defineProperty(row, '__st', { value: st, enumerable: false, configurable: true }); } catch { /* noop */ }
  }
  return st;
}

// Date range like the server's (`from` = start of that day, `to` = end of it).
const inDateRange = (row, params = {}) => {
  if (!params.from && !params.to) return true;
  const d = new Date(row.saleDate || row.date || row.createdAt || 0);
  if (params.from && d < new Date(params.from)) return false;
  if (params.to && d > new Date(`${params.to}T23:59:59`)) return false;
  return true;
};

const filterRows = (rows, params = {}) => {
  let result = [...rows];
  const search = String(params.search || '').trim().toLowerCase();
  if (search) result = result.filter(r => rowSearchText(r).includes(search));
  // The filter dropdowns / date pickers on Sales, Purchases and Returns were
  // ignored offline — the lists never narrowed down.
  if (params.status) result = result.filter(r => r.status === params.status);
  if (params.paymentStatus) result = result.filter(r => r.paymentStatus === params.paymentStatus);
  if (params.paymentMethod) result = result.filter(r => r.paymentMethod === params.paymentMethod);
  if (params.supplier) result = result.filter(r => idOf(r.supplier) === String(params.supplier));
  if (params.from || params.to) result = result.filter(r => inDateRange(r, params));
  return result;
};

// Generic CRUD service factory
// Every service built with this works offline (cached reads, queued writes) —
// see offline/generic.js.
export const createService = createOfflineService;

export const authService = {
  login: (data) => offlineLogin(data),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  getMe: () => cachedGet('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard — computed from local data (sales, purchases, inventory,
// expenses, customers, suppliers) when offline.
// ─────────────────────────────────────────────────────────────────────────────
async function computeDashboardStats() {
  const [sales, purchases, inventory, expenses, customers, suppliers, catalog] = await Promise.all([
    getSales(), getPurchases(), getInventoryAll(), getExpenses(), dbGetAll('customers'), dbGetAll('suppliers'), getMedicineCatalog(),
  ]);
  const activeSales = sales.filter(s => s.status !== 'cancelled');
  const now = new Date();
  const today = now.toDateString();
  const month = now.getMonth(); const year = now.getFullYear();

  const totalSales = activeSales.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
  const dailySales = activeSales.filter(x => new Date(x.saleDate || x.createdAt).toDateString() === today)
    .reduce((s, x) => s + Number(x.totalAmount || 0), 0);
  const monthlySales = activeSales.filter(x => { const d = new Date(x.saleDate || x.createdAt); return d.getMonth() === month && d.getFullYear() === year; })
    .reduce((s, x) => s + Number(x.totalAmount || 0), 0);

  const cogsOf = (sale) => (sale.items || []).reduce((s, it) => {
    const med = catalog.find(m => String(m.medicineId) === String(it.medicine?._id || it.medicine));
    return s + Number(med?.purchasePrice || 0) * Math.max(0, Number(it.quantity || 0) - Number(it.returnedQty || 0));
  }, 0);
  const totalCOGS = activeSales.reduce((s, x) => s + cogsOf(x), 0);
  const monthlyCOGS = activeSales.filter(x => { const d = new Date(x.saleDate || x.createdAt); return d.getMonth() === month && d.getFullYear() === year; })
    .reduce((s, x) => s + cogsOf(x), 0);

  const expensesTotal = expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
  const grossProfit = totalSales - totalCOGS;
  const netProfit = grossProfit - expensesTotal;
  const monthlyProfit = monthlySales - monthlyCOGS;

  const totalDebtors = customers.filter(c => Number(c.currentDebt || c.debt || 0) > 0).length;
  const totalCreditors = suppliers.filter(s => Number(s.currentDebt || s.debt || 0) > 0).length;

  const lowStockItems = inventory.filter(i => Number(i.currentStock || 0) > 0 && Number(i.currentStock || 0) <= Number(i.medicine?.reorderLevel || 10));
  const expiringItems = inventory.filter(i => {
    if (!i.expiryDate) return false;
    const days = (new Date(i.expiryDate) - now) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 90;
  });

  return {
    totalSales, dailySales, monthlySales, totalCOGS, grossProfit, expenses: expensesTotal, netProfit, monthlyProfit,
    totalCustomers: customers.length, totalSuppliers: suppliers.length, totalDebtors, totalCreditors,
    totalEmployees: 0, totalMedicines: catalog.length,
    lowStockItems, expiringItems,
  };
}

async function computeDashboardChart(days = 30) {
  const sales = (await getSales()).filter(s => s.status !== 'cancelled');
  const catalog = await getMedicineCatalog();
  const byDay = new Map();
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(d.getDate() - i);
    byDay.set(d.toISOString().slice(0, 10), { _id: d.toISOString().slice(0, 10), total: 0, cogs: 0, count: 0 });
  }
  for (const sale of sales) {
    const key = new Date(sale.saleDate || sale.createdAt).toISOString().slice(0, 10);
    const bucket = byDay.get(key);
    if (!bucket) continue;
    bucket.total += Number(sale.totalAmount || 0);
    bucket.count += 1;
    bucket.cogs += (sale.items || []).reduce((s, it) => {
      const med = catalog.find(m => String(m.medicineId) === String(it.medicine?._id || it.medicine));
      return s + Number(med?.purchasePrice || 0) * Math.max(0, Number(it.quantity || 0) - Number(it.returnedQty || 0));
    }, 0);
  }
  return [...byDay.values()];
}

export const dashboardService = {
  getStats: async (params) => {
    try { return await api.get('/dashboard/stats', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeDashboardStats(), { offline: true }); }
  },
  getSalesChart: async (params) => {
    try { return await api.get('/dashboard/sales-chart', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeDashboardChart(30), { offline: true }); }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Medicines — full offline CRUD + search (POS)
// ─────────────────────────────────────────────────────────────────────────────
export const medicineService = {
  getAll: async (params = {}) => {
    try {
      const res = await api.get('/medicines', { params });
      await saveMedicineResults(res.data.data || []);
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      let rows = await getMedicineCatalog();
      if (params.isActive !== undefined) {
        const wantActive = params.isActive === true || params.isActive === 'true';
        rows = rows.filter(r => Boolean(r.isActive) === wantActive);
      }
      rows = filterRows(rows, params);
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
  },
  getById: (id) => api.get(`/medicines/${id}`).catch(async e => {
    if (!isNetworkError(e)) throw e;
    const rows = await getMedicineCatalog();
    const row = rows.find(x => String(x.medicineId || x._id) === String(id));
    if (!row) throw e;
    return offlineResponse(row);
  }),
  create: async (data) => {
    try {
      const res = await api.post('/medicines', data);
      const saved = res.data?.data;
      // Without this, a medicine created while online has no row at all in
      // the Sales/POS cache until some unrelated full catalog refresh runs
      // — so an immediate Stock In for it had nothing to attach to and the
      // medicine stayed invisible in Sales search.
      if (saved?._id) await syncMedicineCatalogFields(saved._id, saved);
      return res;
    }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const id = genLocalId('med');
      const row = await upsertMedicineCacheRow({ ...data, medicineId: id, _id: id, offline: true, syncStatus: 'pending' });
      await enqueue({ method: 'POST', url: '/medicines', data: { ...data, clientOfflineId: id } });
      return offlineResponse(row, { offline: true });
    }
  },
  update: async (id, data) => {
    try {
      const res = await api.put(`/medicines/${id}`, data);
      const saved = res.data?.data || { ...data, _id: id };
      // Same reasoning as create() above — a medicine's card (price, base
      // unit, sales units...) can be cached as one row per batch, and the
      // no-batch row gets hidden from search once real batches exist, so
      // this must reach every cached row for the medicine, not just one.
      await syncMedicineCatalogFields(id, saved);
      return res;
    }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await upsertMedicineCacheRow({ ...data, medicineId: id, _id: id, offline: true, syncStatus: 'pending' });
      await enqueue({ method: 'PUT', url: `/medicines/${id}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    try { return await api.delete(`/medicines/${id}`); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      // Medicines are soft-deleted (isActive=false) on the server; mirror that locally.
      await upsertMedicineCacheRow({ medicineId: id, _id: id, isActive: false, syncStatus: 'pending' });
      await enqueue({ method: 'DELETE', url: `/medicines/${id}` });
      return offlineResponse({ _id: id }, { offline: true });
    }
  },
  search: async (q) => {
    try {
      const res = await api.get('/medicines/search', { params: { q } });
      await saveMedicineResults(res.data.data || []);
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse(await searchMedicines(q), { offline: true });
    }
  },
  searchWithBatches: async (q, branch) => {
    // Local cache first — this must answer instantly, no network wait, on
    // every keystroke. The cache is kept fresh automatically now (bulk
    // reference sync runs on every app load / reconnect), so it's rarely
    // stale; a background refresh (below) keeps it that way without making
    // the person wait for a network round-trip on every search.
    const local = await searchMedicines(q, { inStockOnly: true });
    if (navigator.onLine) {
      api.get('/medicines/search-with-batches', { params: { q, branch } })
        .then(async res => { await saveMedicineResults(res.data.data || []); })
        .catch(() => {});
    }
    if (local.length) return offlineResponse(local, { offline: !navigator.onLine, source: 'indexeddb' });
    if (!navigator.onLine) return offlineResponse([], { offline: true });
    try {
      const res = await api.get('/medicines/search-with-batches', { params: { q, branch } });
      await saveMedicineResults(res.data.data || []);
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse([], { offline: true });
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Inventory — full offline stock-in / stock-out / edit / delete / transactions
// ─────────────────────────────────────────────────────────────────────────────
function matchInventoryFilters(rows, params = {}) {
  let result = [...rows];
  const now = new Date();
  if (params.lowStock) result = result.filter(r => Number(r.currentStock || 0) > 0 && Number(r.currentStock || 0) <= Number(r.medicine?.reorderLevel || 10));
  if (params.outOfStock) result = result.filter(r => Number(r.currentStock || 0) <= 0);
  if (params.expired) result = result.filter(r => r.expiryDate && new Date(r.expiryDate) < now);
  // Server: near-expiry means expiring within 3 months AND still in stock.
  if (params.nearExpiry) result = result.filter(r => Number(r.currentStock || 0) > 0 && r.expiryDate && (new Date(r.expiryDate) - now) / 86400000 <= 90 && (new Date(r.expiryDate) - now) >= 0);
  if (params.search) {
    const q = String(params.search).toLowerCase();
    // Was JSON.stringify(r) on every row — serializing the whole object
    // (including nested medicine/category data) on every keystroke is very
    // slow once the inventory has more than a few hundred rows. Searching
    // just the fields a person actually searches by (name, generic name,
    // barcode, batch number) is what was needed and is far faster.
    result = result.filter(r => {
      const hay = [r.medicine?.commercialName, r.medicine?.genericName, r.medicine?.barcode, r.batchNumber]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }
  // Server lists the most recently changed batches first.
  return result.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
}

async function computeInventoryOverview() {
  const rows = await getInventoryAll();
  const now = new Date();
  const totalStock = rows.reduce((s, r) => s + Number(r.currentStock || 0), 0);
  // Server: stock value is at PURCHASE price, low stock means 1..10 units.
  const totalValue = rows.reduce((s, r) => s + Number(r.currentStock || 0) * Number(r.purchasePrice || 0), 0);
  const lowStock = rows.filter(r => Number(r.currentStock || 0) > 0 && Number(r.currentStock || 0) <= 10).length;
  const outOfStock = rows.filter(r => Number(r.currentStock || 0) <= 0).length;
  const expired = rows.filter(r => r.expiryDate && new Date(r.expiryDate) < now).length;
  const nearExpiry = rows.filter(r => r.expiryDate && (new Date(r.expiryDate) - now) / 86400000 <= 90 && (new Date(r.expiryDate) - now) >= 0).length;
  // Server: number of active medicines (not just the ones that have stock rows).
  const totalMedicines = (await getMedicineCatalog()).filter(m => m.isActive !== false).length;
  return { totalMedicines, totalItems: rows.length, totalStock, totalValue, lowStock, outOfStock, expired, nearExpiry };
}

// The offline movement log is written with its own vocabulary ('stock-in',
// 'sale', 'purchase-receive', ...) and without the fields the Transactions
// table reads (type 'stock_in', medicine {commercialName}, baseQuantity, _id).
// Offline, that showed every row red/"STOCK-IN", with no medicine name and a
// missing key. Convert them to the shape the server returns.
const LOCAL_TX_TYPE = {
  'stock-in': 'stock_in', 'purchase-receive': 'stock_in', 'sale-return': 'stock_in',
  'stock-out': 'stock_out', sale: 'stock_out',
};
async function normalizedLocalTx() {
  const [rows, catalog] = await Promise.all([getInventoryTx(), getMedicineCatalog()]);
  const byId = new Map(catalog.map(m => [String(m.medicineId), m]));
  const out = [];
  for (const r of rows) {
    const med = byId.get(String(r.medicineId));
    const type = LOCAL_TX_TYPE[r.type] || r.type;
    const quantity = Number(r.quantity || 0);
    out.push({
      ...r,
      _id: `local-tx-${r.id}`,
      type,
      medicine: { _id: r.medicineId, commercialName: r.medicineName || med?.commercialName || '', genericName: med?.genericName || '' },
      baseQuantity: quantity * await getConversionToBase(r.medicineId, r.unit),
      unit: r.unit || med?.baseUnit || '',
      createdAt: new Date(r.createdAt || Date.now()).toISOString(),
    });
  }
  return out;
}

export const inventoryService = {
  getAll: async (params) => {
    // Local cache first, always — a flaky/slow connection (still reporting
    // "online" but slow to respond) used to make this wait on the full
    // network round-trip (or even the full timeout) before showing
    // anything. Now cached rows render instantly; a background refresh
    // keeps the cache current for next time. Only when the cache is
    // completely empty (nothing synced yet) does this wait on the network.
    let cached = await getInventoryAll();
    // Must update BOTH caches: "inventory" (this page) and "medicines"
    // (what Sales search actually reads stock from) — updating only the
    // former meant newly received stock could show up here but still be
    // unsellable/unsearchable in Sales until the next full reference sync.
    const saveFromServer = async (res) => {
      const rows = res.data.data || [];
      await saveMedicineResults(rows);
      await saveInventoryRows(rows);
    };
    if (cached.length && navigator.onLine) {
      if (await hasPendingQueue()) {
        // There are offline changes the server hasn't received yet. Pulling
        // server numbers now would overwrite them in the cache (the list
        // would jump back to the old stock). Push first — syncQueue() pulls
        // when it's done.
        syncQueue().catch(() => {});
      } else {
        // The refresh used to be fire-and-forget: the list was rendered from
        // the cache right away and the refreshed numbers only appeared the
        // NEXT time the page was opened, so stock changed on another device
        // (or by a sale) never showed up. Wait for it, but never longer than
        // a couple of seconds on a slow connection.
        const refresh = api.get('/inventory', { params }).then(saveFromServer).catch(() => {});
        await Promise.race([refresh, new Promise(resolve => setTimeout(resolve, 2500))]);
        cached = await getInventoryAll();
      }
    }
    if (cached.length) {
      const rows = matchInventoryFilters(cached, params);
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: !navigator.onLine, source: 'indexeddb' });
    }
    try {
      const res = await api.get('/inventory', { params });
      saveFromServer(res).catch(() => {});
      return res;
    }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse([], { pagination: { total: 0 }, offline: true });
    }
  },
  getOverview: async (params) => {
    try { return await api.get('/inventory/overview', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeInventoryOverview(), { offline: true }); }
  },
  stockIn: async (input) => {
    // One id per user action, sent with every attempt (the online try AND the
    // queued retry). If the first request reached the server but its answer
    // was lost (timeout), the retry used to add the stock a second time; the
    // server now recognises the id and ignores the repeat.
    const data = { ...input, clientRequestId: input.clientRequestId || genLocalId('req') };
    try {
      const res = await api.post('/inventory/stock-in', data);
      // Same reasoning as updateRecord below: getAll() answers from cache
      // first, so a successful stock-in must also land in that cache right
      // away or the list keeps showing the old quantity until some other
      // background refresh happens to run. The endpoint's response is the
      // transaction record, not an inventory summary row, so apply the same
      // local movement math the offline path already uses below instead of
      // upserting that response directly.
      await applyInventoryMovement({
        medicineId: data.medicineId, batchNumber: data.batchNumber, quantity: Number(data.quantity),
        unit: data.unit, direction: 1, purchasePrice: data.purchasePrice, sellingPrice: data.sellingPrice,
        expiryDate: data.expiryDate, productionDate: data.productionDate, notes: data.notes,
      });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await applyInventoryMovement({
        medicineId: data.medicineId, batchNumber: data.batchNumber, quantity: Number(data.quantity),
        unit: data.unit, direction: 1, purchasePrice: data.purchasePrice, sellingPrice: data.sellingPrice,
        expiryDate: data.expiryDate, productionDate: data.productionDate, notes: data.notes,
      });
      await addInventoryTx({ type: 'stock-in', ...data, offline: true });
      await enqueue({ method: 'POST', url: '/inventory/stock-in', data: { ...data, clientOfflineId: row._id } });
      return offlineResponse(row, { offline: true });
    }
  },
  stockOut: async (input) => {
    const data = { ...input, clientRequestId: input.clientRequestId || genLocalId('req') };
    try {
      const res = await api.post('/inventory/stock-out', data);
      await applyInventoryMovement({
        medicineId: data.medicineId, batchNumber: data.batchNumber, quantity: Number(data.quantity),
        unit: data.unit, direction: -1,
      });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      // The server refuses a stock-out bigger than what's available. Offline
      // we used to accept anything (the local number just stopped at 0) and
      // queue it — it was then rejected at sync time and sat in the queue
      // forever while the screen showed stock that never really left. Refuse
      // it up front instead, like the server would.
      const available = await getLocalStockBase(data.medicineId, data.batchNumber);
      const needed = Number(data.quantity) * await getConversionToBase(data.medicineId, data.unit);
      if (available !== null && available < needed) {
        const err = new Error('Insufficient stock');
        err.response = { status: 400, data: { success: false, message: `Insufficient stock. Available: ${available}` } };
        throw err;
      }
      const row = await applyInventoryMovement({
        medicineId: data.medicineId, batchNumber: data.batchNumber, quantity: Number(data.quantity),
        unit: data.unit, direction: -1,
      });
      await addInventoryTx({ type: 'stock-out', ...data, offline: true });
      await enqueue({ method: 'POST', url: '/inventory/stock-out', data });
      return offlineResponse(row, { offline: true });
    }
  },
  getTransactions: async (params) => {
    try { return await api.get('/inventory/transactions', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const rows = await normalizedLocalTx();
      const p = paginate(params?.type ? rows.filter(r => r.type === params.type) : rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
  },
  deleteRecord: async (id) => {
    // The Sales search reads stock from its own cache — deleting only the
    // inventory row left the deleted batch sellable.
    const removeLocally = async () => {
      const existing = await getInventoryById(id);
      await removeInventoryRow(id);
      if (existing?.medicineId) await dropMedicineBatchCache(existing.medicineId, existing.batchNumber);
    };
    if (isOfflineId(id)) {
      await removeLocally();
      await purgeQueueReferencing(id);
      return offlineResponse({ _id: id, deleted: true }, { offline: true });
    }
    try {
      const res = await api.delete(`/inventory/summary/${id}`);
      await removeLocally();
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      await removeLocally();
      await enqueue({ method: 'DELETE', url: `/inventory/summary/${id}` });
      return offlineResponse({ _id: id }, { offline: true });
    }
  },
  exportOutOfStock: () => api.get('/inventory/export/out-of-stock', { responseType: 'blob' }),
  exportNearExpiryExcel: () => api.get('/inventory/export/near-expiry/excel', { responseType: 'blob' }),
  exportNearExpiryPdf: () => api.get('/inventory/export/near-expiry/pdf'),
  updateRecord: async (id, data) => {
    // The edit form always sends the stock number it is showing. If that number
    // wasn't changed, sending it anyway makes the server "correct" the stock to
    // a possibly outdated figure (sales / stock-ins done elsewhere meanwhile are
    // wiped out by a price-only edit). Only send it when the person changed it.
    const cached = await getInventoryById(id);
    const payload = { ...data };
    if (cached && payload.currentStock !== undefined && Number(payload.currentStock) === Number(cached.currentStock)) {
      delete payload.currentStock;
    }
    // Mirrors an edit into the inventory list AND the Sales search cache
    // (keyed by medicine + batch). A renamed batch must leave its old key
    // behind, and a new expiry date has to reach the search cache too.
    const applyLocally = async (row, extra = {}) => {
      await upsertInventoryRow(row);
      const medicineId = row.medicineId || row.medicine?._id || row.medicine;
      if (!medicineId) return;
      if (cached && String(cached.batchNumber || '') !== String(row.batchNumber || '')) {
        await dropMedicineBatchCache(medicineId, cached.batchNumber);
      }
      await upsertMedicineCacheRow({
        medicineId, batchNumber: row.batchNumber || '',
        availableQty: row.currentStock, currentStock: row.currentStock,
        purchasePrice: row.purchasePrice, sellingPrice: row.sellingPrice,
        ...(row.expiryDate !== undefined ? { expiryDate: row.expiryDate } : {}),
        ...extra,
      });
    };
    const doOfflineUpdate = async () => {
      const existing = cached || { _id: id };
      const merged = { ...existing, ...data, _id: id, syncStatus: 'pending', updatedAt: nowISO() };
      await applyLocally(merged);
      await enqueue({ method: 'PUT', url: `/inventory/summary/${id}`, data: payload });
      // upsertInventoryRow() resolves to an IndexedDB key, not the saved row
      // — return the actual merged object instead, matching the online path.
      return offlineResponse(merged, { offline: true });
    };
    if (isOfflineId(id)) return doOfflineUpdate();
    try {
      const res = await api.put(`/inventory/summary/${id}`, payload);
      const saved = res.data?.data || { ...data, _id: id };
      // getAll() answers from the local cache first (for speed), so an online
      // save that only updates the server left the list showing the old values
      // right after "successfully" saving; Sales search needs the same update.
      await applyLocally(saved);
      return res;
    }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      return doOfflineUpdate();
    }
  },
  getStats: (medicineId, params) => cachedGet(`/inventory/stats/${medicineId}`, params),
};

// ─────────────────────────────────────────────────────────────────────────────
// Purchases — full offline create/update/approve/receive/cancel/payment.
// Receiving a purchase increases local inventory & the POS cache.
// ─────────────────────────────────────────────────────────────────────────────
// The purchase form only ever sends the supplier's id (selectedSupplier._id)
// — every view of a purchase (list, detail, print) expects a populated
// {_id, name, company} object, the way the server always returns it. Offline,
// nothing populates that id, so without this the Supplier column stays blank
// for any purchase created or edited while offline.
async function resolveOfflineSupplier(supplierField) {
  if (supplierField && typeof supplierField === 'object') return supplierField;
  const supplierId = supplierField;
  return (await get('suppliers', supplierId)) || supplierId;
}

export const purchaseService = {
  getAll: async (params) => {
    // Local cache first (see inventoryService.getAll above for why) — a
    // slow/flaky connection must never block this page from showing what's
    // already synced. Background refresh keeps it current.
    const cached = await getPurchases();
    if (navigator.onLine) {
      api.get('/purchases', { params }).then(res => savePurchases(res.data.data || [])).catch(() => {});
    }
    if (cached.length) {
      let rows = filterRows(cached, params);
      rows = rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: !navigator.onLine, source: 'indexeddb' });
    }
    try { return await api.get('/purchases', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse([], { pagination: { total: 0 }, offline: true });
    }
  },
  getById: async (id) => {
    try { return await api.get(`/purchases/${id}`); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await get('purchases', id);
      if (!row) throw e;
      return offlineResponse(row, { offline: true });
    }
  },
  getStats: async (params) => {
    try { return await api.get('/purchases/stats', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const rows = (await getPurchases()).filter(x => x.status !== 'cancelled' && inDateRange(x, params));
      const totalAmount = rows.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
      const paidAmount = rows.reduce((s, x) => s + Number(x.paidAmount || 0), 0);
      return offlineResponse({ count: rows.length, totalAmount, paidAmount, dueAmount: totalAmount - paidAmount }, { offline: true });
    }
  },
  create: async (data) => {
    try {
      const res = await api.post('/purchases', data);
      // getAll() reads the local cache first now (see above) — without also
      // saving the new purchase here, a PO created successfully on the
      // server still wouldn't show up in the list until whatever background
      // refresh happens to run and finish first. This is the "purchase was
      // created but doesn't appear in the list" bug.
      await savePurchase(res.data?.data || { ...data, _id: genLocalId('pur') });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      // Server rule: the same supplier invoice number can't be used twice.
      if (data.supplierInvoiceNumber) {
        const dup = (await getPurchases()).find(p => p.status !== 'cancelled'
          && idOf(p.supplier) === idOf(data.supplier) && p.supplierInvoiceNumber === data.supplierInvoiceNumber);
        if (dup) throw apiError(`Invoice number "${data.supplierInvoiceNumber}" already exists for this supplier (PO: ${dup.poNumber})`);
      }
      const id = genLocalId('pur');
      const supplier = await resolveOfflineSupplier(data.supplier);
      const purchase = {
        ...data, _id: id, supplier, poNumber: `OFF-PO-${Date.now()}`, status: 'pending',
        paymentStatus: 'unpaid', paidAmount: 0, createdAt: nowISO(), updatedAt: nowISO(), offline: true, syncStatus: 'pending',
      };
      await savePurchase(purchase);
      // The server keeps `createdAt` from the body (that's how an offline order
      // keeps the day it was really made) — it wasn't being sent, so every
      // offline purchase got the day it happened to sync.
      await enqueue({ method: 'POST', url: '/purchases', data: { ...data, createdAt: purchase.createdAt, clientOfflineId: id } });
      return offlineResponse(purchase, { offline: true });
    }
  },
  update: async (id, data) => {
    const assertEditable = async () => {
      const cur = await get('purchases', id);
      if (cur && ['received', 'cancelled'].includes(cur.status)) throw apiError(`Cannot edit a ${cur.status} purchase order`);
    };
    if (isOfflineId(id)) {
      await assertEditable();
      const old = await get('purchases', id) || {};
      const supplier = data.supplier !== undefined ? await resolveOfflineSupplier(data.supplier) : old.supplier;
      const purchase = { ...old, ...data, supplier, _id: id, updatedAt: nowISO(), offline: true, syncStatus: 'pending' };
      await savePurchase(purchase);
      // این خرید هنوز خودش صف POST دارد؛ همان payload صف را با نسخه‌ی ادیت‌شده جایگزین کن
      // به‌جای افزودن یک PUT جداگانه‌ی بی‌فایده روی idای که هنوز روی سرور وجود ندارد.
      const queue = await getQueue();
      const createItem = queue.find(q => q.method === 'POST' && q.url === '/purchases' && q.data?.clientOfflineId === id);
      if (createItem) await updateQueueItem(createItem.id, { ...createItem, data: { ...data, createdAt: old.createdAt || createItem.data?.createdAt, clientOfflineId: id } });
      else await enqueue({ method: 'PUT', url: `/purchases/${id}`, data });
      return offlineResponse(purchase, { offline: true });
    }
    try {
      const res = await api.put(`/purchases/${id}`, data);
      const old = await get('purchases', id) || {};
      await savePurchase(res.data?.data || { ...old, ...data, _id: id, updatedAt: nowISO() });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      await assertEditable();
      const old = await get('purchases', id) || {};
      const supplier = data.supplier !== undefined ? await resolveOfflineSupplier(data.supplier) : old.supplier;
      const purchase = { ...old, ...data, supplier, _id: id, updatedAt: nowISO(), offline: true, syncStatus: 'pending' };
      await savePurchase(purchase);
      await enqueue({ method: 'PUT', url: `/purchases/${id}`, data });
      return offlineResponse(purchase, { offline: true });
    }
  },
  approve: async (id) => {
    // Server: only a pending order can be approved (approving twice is fine).
    const assertApprovable = async () => {
      const cur = await get('purchases', id);
      if (cur?.status && !['pending', 'approved'].includes(cur.status)) throw apiError(`Status is already ${cur.status}`);
      return cur;
    };
    if (isOfflineId(id)) {
      const old = await assertApprovable() || { _id: id };
      const purchase = { ...old, status: 'approved', updatedAt: nowISO(), syncStatus: 'pending' };
      await savePurchase(purchase);
      await enqueue({ method: 'PUT', url: `/purchases/${id}/approve` });
      return offlineResponse(purchase, { offline: true });
    }
    try {
      const res = await api.put(`/purchases/${id}/approve`);
      const old = await get('purchases', id) || { _id: id };
      await savePurchase(res.data?.data || { ...old, status: 'approved', updatedAt: nowISO() });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      const old = await assertApprovable() || { _id: id };
      const purchase = { ...old, status: 'approved', updatedAt: nowISO(), syncStatus: 'pending' };
      await savePurchase(purchase);
      await enqueue({ method: 'PUT', url: `/purchases/${id}/approve` });
      return offlineResponse(purchase, { offline: true });
    }
  },
  receive: async (id) => {
    const doOfflineReceive = async () => {
      const old = await get('purchases', id) || { _id: id, items: [] };
      // Server: receiving twice is a no-op, a cancelled order can't be received.
      if (old.status === 'received') return offlineResponse(old, { offline: true });
      if (old.status === 'cancelled') throw apiError('Cannot receive a cancelled order');
      await enqueue({ method: 'PUT', url: `/purchases/${id}/receive` });
      await bestEffort(async () => {
      for (const item of old.items || []) {
        await applyInventoryMovement({
          medicineId: item.medicine, medicineName: item.medicineName, batchNumber: item.batchNumber,
          quantity: Number(item.quantity), unit: item.unit, direction: 1,
          purchasePrice: item.unitPrice, sellingPrice: item.sellingPrice, expiryDate: item.expiryDate, productionDate: item.productionDate,
        });
        await addInventoryTx({ type: 'purchase-receive', medicineId: item.medicine, medicineName: item.medicineName, quantity: item.quantity, unit: item.unit, batchNumber: item.batchNumber, purchaseId: id, offline: true });
      }
      // Server: the unpaid part of the order becomes debt to the supplier.
      await adjustDebt('suppliers', old.supplier, Math.max(0, Number(old.totalAmount || 0) - Number(old.paidAmount || 0)));
      });
      const purchase = { ...old, status: 'received', receivedAt: nowISO(), updatedAt: nowISO(), syncStatus: 'pending' };
      await savePurchase(purchase);
      return offlineResponse(purchase, { offline: true });
    };
    if (isOfflineId(id)) return doOfflineReceive();
    try {
      const res = await api.put(`/purchases/${id}/receive`);
      const old = await get('purchases', id) || { _id: id };
      await savePurchase(res.data?.data || { ...old, status: 'received', receivedAt: nowISO(), updatedAt: nowISO() });
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflineReceive(); }
  },
  cancel: async (id, data) => {
    const doOfflineCancel = async () => {
      const old = await get('purchases', id) || { _id: id };
      // Server: a received order can't be cancelled (use a purchase return);
      // before, offline reversed the stock and queued a cancel the server
      // then rejected forever.
      if (old.status === 'received') throw apiError('Cannot cancel a received order. Use return instead.');
      if (old.status === 'cancelled') return offlineResponse(old, { offline: true });
      const purchase = { ...old, status: 'cancelled', cancelReason: data?.reason, updatedAt: nowISO(), syncStatus: 'pending' };
      await savePurchase(purchase);
      await enqueue({ method: 'PUT', url: `/purchases/${id}/cancel`, data });
      return offlineResponse(purchase, { offline: true });
    };
    if (isOfflineId(id)) return doOfflineCancel();
    try {
      const res = await api.put(`/purchases/${id}/cancel`, data);
      const old = await get('purchases', id) || { _id: id };
      await savePurchase(res.data?.data || { ...old, status: 'cancelled', cancelReason: data?.reason, updatedAt: nowISO() });
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflineCancel(); }
  },
  recordPayment: async (id, data) => {
    const doOfflinePayment = async () => {
      const old = await get('purchases', id) || { _id: id, paidAmount: 0 };
      const amount = Number(data.amount);
      if (!amount || amount <= 0) throw apiError('Invalid amount');
      const remaining = Number(old.totalAmount || 0) - Number(old.paidAmount || 0);
      if (old.totalAmount !== undefined && amount > remaining + 0.01) throw apiError(`Max payable: ${remaining}`);
      const paidAmount = Math.min(Number(old.totalAmount ?? Infinity), Number(old.paidAmount || 0) + amount);
      const paymentStatus = paidAmount >= Number(old.totalAmount || 0) ? 'paid' : 'partial';
      const purchase = { ...old, paidAmount, paymentStatus, updatedAt: nowISO(), syncStatus: 'pending' };
      await savePurchase(purchase);
      // Server: paying lowers what we owe the supplier (only once it's received).
      if (old.status === 'received') await adjustDebt('suppliers', old.supplier, -amount);
      await enqueue({ method: 'POST', url: `/purchases/${id}/payment`, data });
      return offlineResponse(purchase, { offline: true });
    };
    if (isOfflineId(id)) return doOfflinePayment();
    try {
      const res = await api.post(`/purchases/${id}/payment`, data);
      const old = await get('purchases', id) || { _id: id, paidAmount: 0 };
      const paidAmount = Number(old.paidAmount || 0) + Number(data.amount || 0);
      const paymentStatus = paidAmount >= Number(old.totalAmount || 0) ? 'paid' : 'partial';
      await savePurchase(res.data?.data || { ...old, paidAmount, paymentStatus, updatedAt: nowISO() });
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflinePayment(); }
  },
  delete: async (id) => {
    // خرید‌هایی که فقط آفلاین ساخته شده و هنوز هیچ‌وقت با سرور سنس نشدن،
    // روی سرور اصلاً وجود ندارن — فرستادن DELETE به سرور همیشه fail می‌شد
    // و رکورد محلی هم هیچ‌وقت پاک نمی‌شد. این‌ها را مستقیم و همیشه محلی حذف کن.
    if (isOfflineId(id)) {
      await remove('purchases', id);
      await purgeQueueReferencing(id);
      return offlineResponse({ _id: id, deleted: true }, { offline: true });
    }
    try {
      const res = await api.delete(`/purchases/${id}`);
      await remove('purchases', id);
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      await remove('purchases', id);
      await purgeQueueReferencing(id);
      await enqueue({ method: 'DELETE', url: `/purchases/${id}` });
      return offlineResponse({ _id: id, deleted: true }, { offline: true });
    }
  },
  uploadImages: (id, fd) => api.post(`/purchases/${id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const supplierPaymentService = {
  getPayments: (supplierId, params) => cachedGet(`/suppliers/${supplierId}/payments`, params),
  addPayment: (supplierId, data) => offlineAction('POST', `/suppliers/${supplierId}/payments`, data, { localRow: data }),
  updatePayment: (supplierId, paymentId, data) => offlineAction('PUT', `/suppliers/${supplierId}/payments/${paymentId}`, data),
  deletePayment: (supplierId, paymentId) => offlineAction('DELETE', `/suppliers/${supplierId}/payments/${paymentId}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Sales — full offline create/update/delete/payment; inventory is kept in
// sync (decremented on sale, restored if a sale is cancelled offline).
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Offline sales / purchases / returns must follow the same rules the server
// applies, otherwise something that works offline is rejected at sync time
// (and sits in the queue forever) or leaves different numbers on screen.
// ─────────────────────────────────────────────────────────────────────────────
const apiError = (message, status = 400) => {
  const e = new Error(message);
  e.response = { status, data: { success: false, message } };
  return e;
};
const idOf = (m) => String(m?._id || m || '');

// Stock (base units) the local inventory has for a medicine, summed over its
// batches like the server does. null = nothing cached → unknown, don't block.
async function localAvailable(medicineId) {
  const rows = (await getInventoryAll()).filter(r => r.medicineId === medicineId);
  if (rows.length) return rows.reduce((sum, r) => sum + Math.max(0, Number(r.currentStock || 0)), 0);
  const cached = (await dbGetAll('medicines')).filter(r => r.medicineId === medicineId);
  if (cached.length) return cached.reduce((sum, r) => sum + Math.max(0, Number(r.availableQty || 0)), 0);
  return null;
}

// Server: "Insufficient stock for X. Available: a, Required: b" (total over
// all batches). `alsoFree` = items of the old invoice being replaced by an edit.
async function assertSaleStock(items = [], alsoFree = []) {
  const need = new Map(); const names = new Map();
  for (const it of items) {
    const id = idOf(it.medicine);
    need.set(id, (need.get(id) || 0) + Number(it.quantity || 0) * await getConversionToBase(id, it.unit));
    names.set(id, it.medicineName);
  }
  const freed = new Map();
  for (const it of alsoFree) {
    const id = idOf(it.medicine);
    freed.set(id, (freed.get(id) || 0) + Number(it.quantity || 0) * await getConversionToBase(id, it.unit));
  }
  for (const [id, required] of need) {
    const have = await localAvailable(id);
    if (have === null) continue;
    const available = have + (freed.get(id) || 0);
    if (available < required) throw apiError(`Insufficient stock for "${names.get(id) || ''}". Available: ${available}, Required: ${required}`);
  }
}

// Server deducts FEFO: nearest expiry batch first, across ALL batches of the
// medicine — not only the batch picked in the cart.
async function deductSaleStock(items = []) {
  for (const it of items) {
    const id = idOf(it.medicine);
    let remaining = Number(it.quantity || 0) * await getConversionToBase(id, it.unit);
    const batches = (await getInventoryAll())
      .filter(r => r.medicineId === id && Number(r.currentStock || 0) > 0)
      .sort((a, b) => (a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity) - (b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity));
    if (!batches.length) {
      await applyInventoryMovement({ medicineId: id, medicineName: it.medicineName, batchNumber: it.batchNumber, quantity: remaining, direction: -1 });
      continue;
    }
    for (const b of batches) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, Number(b.currentStock));
      remaining -= take;
      await applyInventoryMovement({ medicineId: id, medicineName: it.medicineName, batchNumber: b.batchNumber, quantity: take, direction: -1 });
    }
  }
}

// Server restores to the line's own batch.
async function restoreSaleStock(items = []) {
  for (const it of items) {
    await applyInventoryMovement({
      medicineId: idOf(it.medicine), medicineName: it.medicineName, batchNumber: it.batchNumber,
      quantity: Number(it.quantity || 0), unit: it.unit, direction: 1,
    });
  }
}

// Customer / supplier balance (server field: totalDebt).
async function adjustDebt(store, id, delta) {
  if (!id || !delta) return;
  const row = await get(store, idOf(id));
  if (!row) return;
  row.totalDebt = Math.max(0, Number(row.totalDebt || 0) + delta);
  await put(store, row);
}

// Local bookkeeping that must never undo/lose an already-queued operation.
const bestEffort = async (fn) => { try { await fn(); } catch (e) { console.warn('offline: local update failed', e); } };

const payStatusOf = (paid, total) => (paid <= 0 ? 'unpaid' : paid >= total ? 'paid' : 'partial');

export const saleService = {
  getAll: async (params = {}) => {
    // Local cache first (see inventoryService.getAll above for why).
    const cached = await getSales();
    if (navigator.onLine) {
      api.get('/sales', { params }).then(res => saveSales(res.data.data || [])).catch(() => {});
    }
    if (cached.length) {
      const rows = filterRows(cached, params).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: !navigator.onLine, source: 'indexeddb' });
    }
    try { return await api.get('/sales', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse([], { pagination: { total: 0 }, offline: true });
    }
  },
  getById: async (id) => {
    try { return await api.get(`/sales/${id}`); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await get('sales', id);
      if (!row) throw e;
      return offlineResponse(row, { offline: true });
    }
  },
  create: async (data) => {
    try {
      const res = await api.post('/sales', data);
      // getAll() reads the local cache first now (see above) — without also
      // saving the new sale here, a sale created successfully on the server
      // still wouldn't show up in the list until whatever background
      // refresh happens to run and finish first.
      await saveSale(res.data?.data || { ...data, _id: genLocalId('sale') });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      // Same check the server makes — refuse now instead of queueing a sale
      // that would be rejected at sync time and never go through.
      await assertSaleStock(data.items);
      const id = genLocalId('sale');
      const now = nowISO();
      // If the user didn't pick a date explicitly, "now" (the actual moment of
      // the offline sale) is the sale date. This must be resolved once, here,
      // and reused for BOTH the local record and the queued payload — the
      // backend falls back to "new Date()" itself when saleDate is missing,
      // and that fallback would otherwise run at sync time (whenever the app
      // reconnects, possibly hours or days later), silently replacing the real
      // sale date with the sync date once the sale reaches the server.
      const resolvedSaleDate = data.saleDate || now;
      const sale = {
        ...data, _id: id, invoiceNumber: `OFF-${Date.now()}`, status: 'completed',
        createdAt: now, updatedAt: now, saleDate: resolvedSaleDate,
        offline: true, syncStatus: 'pending',
        customer: data.customer ? { _id: data.customer, name: data.customerName || '' } : undefined,
        subtotal: Number(data.subtotal || 0), discountAmount: Number(data.discountAmount || 0),
        taxAmount: Number(data.taxAmount || 0), totalAmount: Number(data.totalAmount || 0),
        paidAmount: Number(data.paidAmount || 0), changeAmount: Number(data.changeAmount || 0),
      };
      // The queue entry is what actually gets the sale to the server — write it
      // FIRST. If updating the local stock/debt below ever threw, the sale used
      // to be shown as saved but was never queued, so it never synced.
      await enqueue({ method: 'POST', url: '/sales', data: { ...data, saleDate: resolvedSaleDate, clientOfflineId: id } });
      await saveSale(sale);
      await bestEffort(async () => {
      await deductSaleStock(sale.items);
      await adjustDebt('customers', data.customer, Math.max(0, sale.totalAmount - sale.paidAmount));
      for (const item of sale.items || []) {
        await addInventoryTx({ type: 'sale', medicineId: item.medicine, medicineName: item.medicineName, quantity: item.quantity, unit: item.unit, batchNumber: item.batchNumber, saleId: id, offline: true });
      }
      });
      return offlineResponse(sale, { offline: true });
    }
  },
  update: async (id, data) => {
    const doOfflineUpdate = async () => {
      const old = await get('sales', id);
      if (old?.status === 'cancelled') throw apiError('Cannot edit cancelled sale');
      // Mirror the server: give the old lines back, take the new ones (FEFO),
      // and move the customer's balance from the old unpaid to the new unpaid.
      let extra = {};
      if (old && data.items) {
        await assertSaleStock(data.items, old.items);
        await restoreSaleStock(old.items);
        await deductSaleStock(data.items);
      }
      if (old) {
        const newTotal = data.totalAmount !== undefined ? Number(data.totalAmount) : Number(old.totalAmount || 0);
        const newPaid = data.paidAmount !== undefined ? Number(data.paidAmount) : Number(old.paidAmount || 0);
        await adjustDebt('customers', old.customer, -Math.max(0, Number(old.totalAmount || 0) - Number(old.paidAmount || 0)));
        await adjustDebt('customers', data.customer ?? old.customer, Math.max(0, newTotal - newPaid));
        extra = { paymentStatus: payStatusOf(newPaid, newTotal) };
      }
      const sale = { ...(old || {}), ...data, ...extra, _id: id, updatedAt: nowISO(), offline: true, syncStatus: 'pending' };
      await saveSale(sale);
      if (isOfflineId(id)) {
        const queue = await getQueue();
        const createItem = queue.find(q => q.method === 'POST' && q.url === '/sales' && q.data?.clientOfflineId === id);
        // keep the sale date fixed when the queued sale is replaced by its edit —
        // the edit form leaves it empty and the server would use the sync day
        if (createItem) { await updateQueueItem(createItem.id, { ...createItem, data: { ...data, saleDate: data.saleDate || createItem.data?.saleDate || old?.saleDate, clientOfflineId: id } }); return offlineResponse(sale, { offline: true }); }
      }
      await enqueue({ method: 'PUT', url: `/sales/${id}`, data });
      return offlineResponse(sale, { offline: true });
    };
    if (isOfflineId(id)) return doOfflineUpdate();
    try {
      const res = await api.put(`/sales/${id}`, data);
      const old = await get('sales', id) || {};
      await saveSale(res.data?.data || { ...old, ...data, _id: id, updatedAt: nowISO() });
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflineUpdate(); }
  },
  delete: async (id) => {
    const doOfflineDelete = async () => {
      const old = await get('sales', id);
      if (old && old.status !== 'cancelled') {
        await restoreSaleStock(old.items);
        await adjustDebt('customers', old.customer, -Math.max(0, Number(old.totalAmount || 0) - Number(old.paidAmount || 0)));
      }
      // Online, the server deletes the sale for good and the list drops it —
      // keeping a "cancelled" copy here made offline look different (and the
      // copy never went away after sync).
      await remove('sales', id);
      if (isOfflineId(id)) {
        // این فروش هنوز روی سرور ساخته نشده — همان‌جا (queue) لغوش کن، به‌جای
        // فرستادن یک DELETE جداگانه روی idای که سرور اصلاً نمی‌شناسدش.
        await purgeQueueReferencing(id);
        return offlineResponse({ _id: id, status: 'cancelled' }, { offline: true });
      }
      await enqueue({ method: 'DELETE', url: `/sales/${id}` });
      return offlineResponse(old || { _id: id, status: 'cancelled' }, { offline: true });
    };
    if (isOfflineId(id)) return doOfflineDelete();
    try {
      const res = await api.delete(`/sales/${id}`);
      // The server permanently deletes the sale here (unlike purchases'
      // cancel-style endpoints) — mirror that in the cache too, or the
      // "deleted" sale keeps showing up in the cache-first list.
      await remove('sales', id);
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflineDelete(); }
  },
  recordPayment: async (id, data) => {
    const doOfflinePayment = async () => {
      const old = await get('sales', id) || { _id: id, paidAmount: 0 };
      const amount = Number(data.amount);
      if (!amount || amount <= 0) throw apiError('Invalid amount');
      const remaining = Number(old.totalAmount || 0) - Number(old.paidAmount || 0);
      if (old.totalAmount !== undefined && amount > remaining) throw apiError(`Max payable: ${remaining}`);
      const paidAmount = Number(old.paidAmount || 0) + amount;
      const sale = { ...old, paidAmount, paymentStatus: payStatusOf(paidAmount, Number(old.totalAmount || 0)), updatedAt: nowISO(), syncStatus: 'pending' };
      await saveSale(sale);
      await adjustDebt('customers', old.customer, -amount);
      await enqueue({ method: 'POST', url: `/sales/${id}/payment`, data });
      return offlineResponse(sale, { offline: true });
    };
    if (isOfflineId(id)) return doOfflinePayment();
    try {
      const res = await api.post(`/sales/${id}/payment`, data);
      const old = await get('sales', id) || { _id: id, paidAmount: 0 };
      await saveSale(res.data?.data || { ...old, paidAmount: Number(old.paidAmount || 0) + Number(data.amount || 0), updatedAt: nowISO() });
      return res;
    } catch (e) { if (!isNetworkError(e)) throw e; return doOfflinePayment(); }
  },
  getStats: async (params = {}) => {
    try { return await api.get('/sales/stats', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const rows = (await getSales()).filter(x => x.status !== 'cancelled' && inDateRange(x, params));
      const revenue = rows.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
      const paid = rows.reduce((s, x) => s + Number(x.paidAmount || 0), 0);
      const now = new Date(); const today = now.toDateString(); const month = now.getMonth(); const year = now.getFullYear();
      const todayRows = rows.filter(x => new Date(x.saleDate || x.createdAt).toDateString() === today);
      const monthRows = rows.filter(x => { const d = new Date(x.saleDate || x.createdAt); return d.getMonth() === month && d.getFullYear() === year; });
      return offlineResponse({ totals: { count: rows.length, revenue, paid }, today: { count: todayRows.length, revenue: todayRows.reduce((s, x) => s + Number(x.totalAmount || 0), 0) }, month: { count: monthRows.length, revenue: monthRows.reduce((s, x) => s + Number(x.totalAmount || 0), 0) } }, { offline: true });
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Suppliers / Customers — read + create work offline
// ─────────────────────────────────────────────────────────────────────────────
export const supplierService = {
  getAll: async (params = {}) => {
    try { const res = await api.get('/suppliers', { params }); await saveSuppliers(res.data.data || []); return res; }
    catch (e) { if (!isNetworkError(e)) throw e; const rows = filterRows(await dbGetAll('suppliers'), params); const p = paginate(rows, params); return offlineResponse(p.data, { pagination: p.pagination, offline: true }); }
  },
  search: async (q) => {
    try { return await api.get('/suppliers/search', { params: { q } }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await searchSuppliers(q), { offline: true }); }
  },
  getById: async (id) => {
    try { return await api.get(`/suppliers/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; const row = await get('suppliers', id); if (!row) throw e; return offlineResponse(row, { offline: true }); }
  },
  create: async (data) => {
    try { return await api.post('/suppliers', data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const id = genLocalId('sup');
      const row = { ...data, _id: id, offline: true, syncStatus: 'pending' };
      await put('suppliers', row);
      await enqueue({ method: 'POST', url: '/suppliers', data: { ...data, clientOfflineId: id } });
      return offlineResponse(row, { offline: true });
    }
  },
  update: async (id, data) => {
    try { return await api.put(`/suppliers/${id}`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const old = await get('suppliers', id) || { _id: id };
      const row = { ...old, ...data, syncStatus: 'pending' };
      await put('suppliers', row);
      await enqueue({ method: 'PUT', url: `/suppliers/${id}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    try { return await api.delete(`/suppliers/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; await remove('suppliers', id); await enqueue({ method: 'DELETE', url: `/suppliers/${id}` }); return offlineResponse({ _id: id }, { offline: true }); }
  },
};

export const customerService = {
  getAll: async (params = {}) => {
    try { const res = await api.get('/customers', { params }); await saveCustomers(res.data.data || []); return res; }
    catch (e) { if (!isNetworkError(e)) throw e; const rows = filterRows(await dbGetAll('customers'), params); const p = paginate(rows, params); return offlineResponse(p.data, { pagination: p.pagination, offline: true }); }
  },
  getById: async (id) => {
    try { return await api.get(`/customers/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; const row = await get('customers', id); if (!row) throw e; return offlineResponse(row, { offline: true }); }
  },
  create: async (data) => {
    try { return await api.post('/customers', data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const id = genLocalId('cust');
      const row = { ...data, _id: id, offline: true, syncStatus: 'pending' };
      await put('customers', row);
      await enqueue({ method: 'POST', url: '/customers', data: { ...data, clientOfflineId: id } });
      return offlineResponse(row, { offline: true });
    }
  },
  update: async (id, data) => {
    try { return await api.put(`/customers/${id}`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const old = await get('customers', id) || { _id: id };
      const row = { ...old, ...data, syncStatus: 'pending' };
      await put('customers', row);
      await enqueue({ method: 'PUT', url: `/customers/${id}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    try { return await api.delete(`/customers/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; await remove('customers', id); await enqueue({ method: 'DELETE', url: `/customers/${id}` }); return offlineResponse({ _id: id }, { offline: true }); }
  },
};

export const patientService = createService('patients');
export const doctorService = { ...createService('employees'), getAll: (p) => cachedGet('/employees', p) }; // doctors → employees
export const prescriptionService = createService('prescriptions');
export const userService = createService('users');
export const roleService = createService('roles');
export const branchService = createService('branches');

// Category has offline read (getAll) + offline create/update/delete, since
// the Quick Add Medicine modal (Purchases page) needs its dropdown populated
// even with no connection.
export const categoryService = {
  getAll: async (params = {}) => {
    try { const res = await api.get('/categories', { params }); await saveCategories(res.data.data || []); return res; }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const rows = filterRows(await dbGetAll('categories'), params);
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
  },
  getById: async (id) => {
    try { return await api.get(`/categories/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; const row = await get('categories', id); if (!row) throw e; return offlineResponse(row, { offline: true }); }
  },
  create: async (data) => {
    try { return await api.post('/categories', data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const id = genLocalId('cat');
      const row = { ...data, _id: id, offline: true, syncStatus: 'pending' };
      await put('categories', row);
      await enqueue({ method: 'POST', url: '/categories', data: { ...data, clientOfflineId: id } });
      return offlineResponse(row, { offline: true });
    }
  },
  update: async (id, data) => {
    try { return await api.put(`/categories/${id}`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const old = await get('categories', id) || { _id: id };
      const row = { ...old, ...data, syncStatus: 'pending' };
      await put('categories', row);
      await enqueue({ method: 'PUT', url: `/categories/${id}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    try { return await api.delete(`/categories/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; await remove('categories', id); await enqueue({ method: 'DELETE', url: `/categories/${id}` }); return offlineResponse({ _id: id }, { offline: true }); }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Accounting — expenses (full offline CRUD) + summary/profit-loss/cash-flow
// computed locally, + customer/supplier ledgers backed by local payments.
// ─────────────────────────────────────────────────────────────────────────────
async function computeAccountingSummary({ from, to } = {}) {
  const inRange = (d) => (!from || new Date(d) >= new Date(from)) && (!to || new Date(d) <= new Date(to));
  const sales = (await getSales()).filter(s => s.status !== 'cancelled' && inRange(s.saleDate || s.createdAt));
  const purchases = (await getPurchases()).filter(p => p.status !== 'cancelled' && inRange(p.createdAt));
  const expenses = (await getExpenses()).filter(x => inRange(x.date || x.createdAt));
  const revenue = sales.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
  const purchaseCost = purchases.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
  const expenseTotal = expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
  return {
    revenue, purchaseCost, expenses: expenseTotal,
    netProfit: revenue - purchaseCost - expenseTotal,
    salesCount: sales.length, purchasesCount: purchases.length,
  };
}

async function computeProfitLoss({ period = 'day', from, to } = {}) {
  const chart = await computeDashboardChart(30);
  return chart.map(d => ({ period: d._id, revenue: d.total, cogs: d.cogs, profit: d.total - d.cogs }));
}

async function computeCashFlow() {
  const sales = (await getSales()).filter(s => s.status !== 'cancelled');
  const purchases = await getPurchases();
  const expenses = await getExpenses();
  const byDay = new Map();
  const bump = (date, field, amount) => {
    const key = new Date(date).toISOString().slice(0, 10);
    if (!byDay.has(key)) byDay.set(key, { period: key, cashIn: 0, cashOut: 0 });
    byDay.get(key)[field] += amount;
  };
  sales.forEach(s => bump(s.saleDate || s.createdAt, 'cashIn', Number(s.paidAmount || 0)));
  purchases.forEach(p => bump(p.createdAt, 'cashOut', Number(p.paidAmount || 0)));
  expenses.forEach(x => bump(x.date || x.createdAt, 'cashOut', Number(x.amount || 0)));
  return [...byDay.values()].sort((a, b) => a.period.localeCompare(b.period));
}

// Builds a simple running ledger (invoices + payments) for one customer or supplier.
async function buildLedger(type, id) {
  const invoices = type === 'customer'
    ? (await getSales()).filter(s => String(s.customer?._id || s.customer) === String(id))
    : (await getPurchases()).filter(p => String(p.supplier?._id || p.supplier) === String(id));
  const payments = await getLedgerPayments(type, id);
  return { invoices, payments };
}

export const expenseService = {
  getAll: async (params) => {
    try { return await api.get('/accounting/expenses', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; const rows = filterRows(await getExpenses(), params).sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)); const p = paginate(rows, params); return offlineResponse(p.data, { pagination: p.pagination, offline: true }); }
  },
  create: async (data) => {
    try { return await api.post('/accounting/expenses', data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const id = genLocalId('exp');
      const row = { ...data, _id: id, createdAt: nowISO(), offline: true, syncStatus: 'pending' };
      await saveExpense(row);
      await enqueue({ method: 'POST', url: '/accounting/expenses', data: { ...data, clientOfflineId: id } });
      return offlineResponse(row, { offline: true });
    }
  },
  update: async (id, data) => {
    try { return await api.put(`/accounting/expenses/${id}`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const old = await get('expenses', id) || { _id: id };
      const row = { ...old, ...data, syncStatus: 'pending' };
      await saveExpense(row);
      await enqueue({ method: 'PUT', url: `/accounting/expenses/${id}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    try { return await api.delete(`/accounting/expenses/${id}`); }
    catch (e) { if (!isNetworkError(e)) throw e; await removeExpense(id); await enqueue({ method: 'DELETE', url: `/accounting/expenses/${id}` }); return offlineResponse({ _id: id }, { offline: true }); }
  },
};

export const notificationService = {
  getAll: () => cachedGet('/notifications'),
  markRead: (id) => offlineAction('PUT', `/notifications/${id}/read`),
  markAllRead: () => offlineAction('PUT', '/notifications/mark-all-read'),
};
export const auditService = {
  getAll: (params) => cachedGet('/audit', params),
};
export const reportService = {
  getSales: (params) => cachedGet('/reports/sales', params),
  getPurchases: (params) => cachedGet('/reports/purchases', params),
  getInventory: (params) => cachedGet('/reports/inventory', params),
  getExpiry: (params) => cachedGet('/reports/expiry', params),
  getProfit: (params) => cachedGet('/reports/profit', params),
};

export const accountingService = {
  getSummary: async (params) => {
    try { return await api.get('/accounting/summary', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeAccountingSummary(params), { offline: true }); }
  },
  getProfitLoss: async (params) => {
    try { return await api.get('/accounting/profit-loss', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeProfitLoss(params), { offline: true }); }
  },
  getCashFlow: async (params) => {
    try { return await api.get('/accounting/cash-flow', { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await computeCashFlow(), { offline: true }); }
  },

  // Customer accounts
  getCustomerProfile: (id) => cachedGet(`/accounting/customers/${id}/profile`),
  getCustomerAccounts: async (params) => {
    try { return await api.get('/accounting/customers', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const customers = filterRows(await dbGetAll('customers'), params);
      const rows = [];
      for (const c of customers) {
        const { invoices, payments } = await buildLedger('customer', c._id);
        const invoiced = invoices.reduce((s, x) => s + Number(x.totalAmount || 0), 0);
        const paidViaSale = invoices.reduce((s, x) => s + Number(x.paidAmount || 0), 0);
        const paidExtra = payments.reduce((s, x) => s + Number(x.amount || 0), 0);
        const debt = invoiced - paidViaSale - paidExtra;
        if (!params?.hasDebt || debt > 0) rows.push({ ...c, invoicedTotal: invoiced, paidTotal: paidViaSale + paidExtra, debt });
      }
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
  },
  getCustomerInvoices: async (id) => {
    try { return await api.get(`/accounting/customers/${id}/invoices`); }
    catch (e) { if (!isNetworkError(e)) throw e; const { invoices } = await buildLedger('customer', id); return offlineResponse(invoices, { offline: true }); }
  },
  getCustomerPayments: async (id, params) => {
    try { return await api.get(`/accounting/customers/${id}/payments`, { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await getLedgerPayments('customer', id), { offline: true }); }
  },
  addCustomerPayment: async (id, data) => {
    try { return await api.post(`/accounting/customers/${id}/payments`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await addLedgerPayment('customer', id, data);
      await enqueue({ method: 'POST', url: `/accounting/customers/${id}/payments`, data });
      return offlineResponse(row, { offline: true });
    }
  },

  // Supplier accounts
  getSupplierAccounts: async (params) => {
    try { return await api.get('/accounting/suppliers', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const suppliers = filterRows(await dbGetAll('suppliers'), params);
      const rows = [];
      for (const s of suppliers) {
        const { invoices, payments } = await buildLedger('supplier', s._id);
        const invoiced = invoices.reduce((sum, x) => sum + Number(x.totalAmount || 0), 0);
        const paidViaPurchase = invoices.reduce((sum, x) => sum + Number(x.paidAmount || 0), 0);
        const paidExtra = payments.reduce((sum, x) => sum + Number(x.amount || 0), 0);
        const debt = invoiced - paidViaPurchase - paidExtra;
        if (!params?.hasDebt || debt > 0) rows.push({ ...s, invoicedTotal: invoiced, paidTotal: paidViaPurchase + paidExtra, debt });
      }
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
  },
  getSupplierInvoices: async (id) => {
    try { return await api.get(`/accounting/suppliers/${id}/invoices`); }
    catch (e) { if (!isNetworkError(e)) throw e; const { invoices } = await buildLedger('supplier', id); return offlineResponse(invoices, { offline: true }); }
  },
  getSupplierPayments: async (id, params) => {
    try { return await api.get(`/accounting/suppliers/${id}/payments`, { params }); }
    catch (e) { if (!isNetworkError(e)) throw e; return offlineResponse(await getLedgerPayments('supplier', id), { offline: true }); }
  },
  addSupplierPayment: async (id, data) => {
    try { return await api.post(`/accounting/suppliers/${id}/payments`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await addLedgerPayment('supplier', id, data);
      await enqueue({ method: 'POST', url: `/accounting/suppliers/${id}/payments`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  updateSupplierPayment: async (id, paymentId, data) => {
    try { return await api.put(`/accounting/suppliers/${id}/payments/${paymentId}`, data); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const row = await updateLedgerPayment(paymentId, data);
      await enqueue({ method: 'PUT', url: `/accounting/suppliers/${id}/payments/${paymentId}`, data });
      return offlineResponse(row, { offline: true });
    }
  },
  deleteSupplierPayment: async (id, paymentId) => {
    try { return await api.delete(`/accounting/suppliers/${id}/payments/${paymentId}`); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      await removeLedgerPayment(paymentId);
      await enqueue({ method: 'DELETE', url: `/accounting/suppliers/${id}/payments/${paymentId}` });
      return offlineResponse({ _id: paymentId }, { offline: true });
    }
  },

  // Expenses (same endpoints as expenseService, kept for pages that use accountingService directly)
  getExpenses: (params) => expenseService.getAll(params),
  createExpense: (data) => expenseService.create(data),
  updateExpense: (id, d) => expenseService.update(id, d),
  deleteExpense: (id) => expenseService.delete(id),
};

// NEW services
export const brandService = createService('brands');
export const manufacturerService = createService('manufacturers');
export const strengthService = createService('strengths');
export const taxService = createService('taxes');
export const currencyService = createService('currencies');
export const bankService = {
  ...createService('bank-accounts'),
  getTransactions: (id, params) => cachedGet(`/bank-accounts/${id}/transactions`, params),
  addTransaction: (id, data) => offlineAction('POST', `/bank-accounts/${id}/transactions`, data, { localRow: data }),
};
export const requisitionService = {
  ...createService('requisitions'),
  approve: (id) => offlineAction('PUT', `/requisitions/${id}/approve`, undefined, { listEndpoint: 'requisitions', patch: rows => rows.map(r => r._id === id ? { ...r, status: 'approved', syncStatus: 'pending' } : r) }),
  reject: (id) => offlineAction('PUT', `/requisitions/${id}/reject`, undefined, { listEndpoint: 'requisitions', patch: rows => rows.map(r => r._id === id ? { ...r, status: 'rejected', syncStatus: 'pending' } : r) }),
};
export const settingsService = {
  getAll: async (params) => {
    try { const res = await api.get('/settings', { params }); await saveSettingsCache(res.data?.settings || res.data?.data || {}); return res; }
    catch (e) { if (!isNetworkError(e)) throw e; const settings = await getSettingsCache(); return { data: { settings, offline: true } }; }
  },
  updateAll: async (data) => {
    const res = await offlineAction('PUT', '/settings', data);
    if (res.data?.offline) { try { await saveSettingsCache({ ...(await getSettingsCache()), ...data }); } catch { /* noop */ } }
    return res;
  },
  update: (key, data) => offlineAction('PUT', `/settings/${key}`, data),
};

export const employeeService = {
  ...createService('employees'),
  getSalaryHistory: (id) => cachedGet(`/employees/${id}/salary-history`),
  // The salary calculation is computed by the server — needs a connection.
  calculateSalary: (id, data) => api.post(`/employees/${id}/calculate-salary`, data),
  paySalary: (id, data) => offlineAction('POST', `/employees/${id}/pay-salary`, data, { localRow: data }),
  updateSalaryPayment: (id, pid, data) => offlineAction('PUT', `/employees/${id}/salary/${pid}`, data),
  deleteSalaryPayment: (id, pid) => offlineAction('DELETE', `/employees/${id}/salary/${pid}`),
};

export const purchaseRequestService = {
  ...createService('purchase-requests'),
  getOutOfStock: (params) => cachedGet('/purchase-requests/out-of-stock', params),
  convert: (id, data) => offlineAction('POST', `/purchase-requests/${id}/convert`, data),
  cancel: (id) => offlineAction('PUT', `/purchase-requests/${id}/cancel`, undefined, { listEndpoint: 'purchase-requests', patch: rows => rows.map(r => r._id === id ? { ...r, status: 'cancelled', syncStatus: 'pending' } : r) }),
};

export const partnerService = {
  getSummary: () => cachedGet('/partners/summary'),
  getByPartner: (key) => cachedGet(`/partners/${key}`),
  addTransaction: (data) => offlineAction('POST', '/partners/transaction', data, { localRow: data }),
  repay: (data) => offlineAction('POST', '/partners/repay', data, { localRow: data }),
};

async function applySaleReturnLocally(data, totalAmount) {
  const sale = await get('sales', data.saleId);
  if (!sale) return;
  const idOf = (m) => String(m?._id || m);
  for (const ri of data.items || []) {
    const item = (sale.items || []).find(si => idOf(si.medicine) === idOf(ri.medicine));
    if (!item) continue;
    item.returnedQty = Number(item.returnedQty || 0) + Number(ri.quantity || 0);
    await applyInventoryMovement({
      medicineId: idOf(item.medicine), medicineName: item.medicineName, batchNumber: item.batchNumber,
      quantity: Number(ri.quantity || 0), unit: item.unit, direction: 1,
    });
    await addInventoryTx({ type: 'sale-return', medicineId: idOf(item.medicine), medicineName: item.medicineName, quantity: ri.quantity, unit: item.unit, batchNumber: item.batchNumber, saleId: data.saleId, offline: true });
  }
  // Same arithmetic as the server (routes/saleReturn.routes.js).
  const oldTotal = Number(sale.totalAmount || 0);
  const oldPaid = Number(sale.paidAmount || 0);
  const newTotal = Math.max(0, oldTotal - totalAmount);
  const paidReduction = oldTotal > 0 ? Math.min(oldPaid, Math.round(oldPaid * totalAmount / oldTotal)) : 0;
  const newPaid = Math.max(0, oldPaid - paidReduction);
  await saveSale({
    ...sale, totalAmount: newTotal, paidAmount: newPaid,
    paymentStatus: newPaid <= 0 ? 'unpaid' : newPaid >= newTotal ? 'paid' : 'partial',
    updatedAt: nowISO(),
  });
  const customerId = idOf(sale.customer);
  if (sale.customer && data.refundMethod === 'credit') {
    const customer = await get('customers', customerId);
    if (customer) {
      for (const key of ['totalDebt', 'currentDebt', 'debt']) {
        if (customer[key] !== undefined) customer[key] = Math.max(0, Number(customer[key] || 0) - totalAmount);
      }
      await put('customers', customer);
    }
  }
}

export const saleReturnService = {
  getAll: async (params) => {
    // Local cache first (see inventoryService.getAll above for why).
    const cached = await getSaleReturns();
    if (navigator.onLine) {
      api.get('/sale-returns', { params }).then(res => saveSaleReturns(res.data.data || [])).catch(() => {});
    }
    if (cached.length) {
      const rows = filterRows(cached, params).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      const p = paginate(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: !navigator.onLine, source: 'indexeddb' });
    }
    try { return await api.get('/sale-returns', { params }); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      return offlineResponse([], { pagination: { total: 0 }, offline: true });
    }
  },
  getById: async (id) => {
    try { return await api.get(`/sale-returns/${id}`); }
    catch (e) {
      if (!isNetworkError(e)) throw e;
      const rows = await getSaleReturns();
      const row = rows.find(r => r._id === id);
      if (!row) throw e;
      return offlineResponse(row, { offline: true });
    }
  },
  create: async (data) => {
    try {
      const res = await api.post('/sale-returns', data);
      // getAll() reads the local cache first now — without also saving the
      // new return here, a return that saved successfully on the server
      // still wouldn't show up in the Sale Returns list until whatever
      // background refresh happens to run and finish first. This is why
      // the return only ever showed up as a stock-in on Inventory (which
      // reads live) but never in the Sale Returns list itself.
      await saveSaleReturn(res.data?.data || { ...data, _id: genLocalId('ret') });
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      // Same checks as the server, so a return that would be rejected at sync
      // time is refused right now instead of being queued forever.
      if (!data.saleId || !data.items?.length) throw apiError('Sale ID and items required');
      const origin = await get('sales', data.saleId);
      if (origin) {
        for (const ri of data.items) {
          const line = (origin.items || []).find(si => idOf(si.medicine) === idOf(ri.medicine));
          if (!line) throw apiError(`Medicine ${ri.medicine} not in original sale`);
          if (Number(ri.quantity) > Number(line.quantity)) throw apiError(`Return qty exceeds sold qty for ${line.medicineName}`);
        }
      }
      const id = genLocalId('ret');
      // totalAmount is what the Sale Returns list shows — without it an
      // offline return displayed an empty amount until it synced.
      const totalAmount = (data.items || []).reduce((sum, i) => sum + (
        i.refundAmount != null && i.refundAmount !== '' ? Number(i.refundAmount) || 0 : (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0)
      ), 0);
      const row = { ...data, _id: id, returnNumber: `OFF-RET-${Date.now()}`, totalAmount, createdAt: nowISO(), offline: true, syncStatus: 'pending' };
      // Queue first (see the sale above), local effects after, best effort.
      await enqueue({ method: 'POST', url: '/sale-returns', data: { ...data, clientOfflineId: id } });
      await saveSaleReturn(row);
      // Mirror what the server does, locally: the refunded money comes off the
      // sale (so Sales / Dashboard / Accounting go down), the medicine goes back
      // into stock, and a "credit" refund lowers the customer's debt.
      await bestEffort(() => applySaleReturnLocally(data, totalAmount));
      return offlineResponse(row, { offline: true });
    }
  },
  delete: async (id) => {
    // Cancelling a return that only exists offline: just forget it (and what
    // its creation changed locally) — the server never heard of it.
    const reverseLocally = async () => {
      const ret = await get('saleReturns', id);
      if (ret) {
        const sale = await get('sales', ret.saleId || idOf(ret.sale));
        const lines = ret.items || [];
        for (const ri of lines) {
          const line = (sale?.items || []).find(si => idOf(si.medicine) === idOf(ri.medicine));
          await applyInventoryMovement({
            medicineId: idOf(ri.medicine), medicineName: ri.medicineName || line?.medicineName,
            batchNumber: ri.batchNumber ?? line?.batchNumber, quantity: Number(ri.quantity || 0), unit: ri.unit || line?.unit, direction: -1,
          });
          if (line) line.returnedQty = Math.max(0, Number(line.returnedQty || 0) - Number(ri.quantity || 0));
        }
        if (sale) {
          // Server: the refunded amount goes back onto the invoice (total and paid).
          const total = Number(sale.totalAmount || 0) + Number(ret.totalAmount || 0);
          const paid = Number(sale.paidAmount || 0) + Number(ret.totalAmount || 0);
          await saveSale({ ...sale, totalAmount: total, paidAmount: paid, paymentStatus: paid >= total ? 'paid' : paid > 0 ? 'partial' : 'unpaid', updatedAt: nowISO() });
        }
      }
      await remove('saleReturns', id);
    };
    if (isOfflineId(id)) {
      await reverseLocally();
      await purgeQueueReferencing(id);
      return offlineResponse({ _id: id, deleted: true }, { offline: true });
    }
    try {
      const res = await api.delete(`/sale-returns/${id}`);
      await remove('saleReturns', id);
      return res;
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      await reverseLocally();
      await enqueue({ method: 'DELETE', url: `/sale-returns/${id}` });
      return offlineResponse({ _id: id, deleted: true }, { offline: true });
    }
  },
};

export const backupService = {
  list: () => api.get('/backup/list'),
  create: () => api.post('/backup/create'),
  download: (fileName) => api.get(`/backup/download/${fileName}`, { responseType: 'blob' }),
};

export const purchaseReturnService = {
  ...createOfflineService('purchase-returns'),
};

// Offline sync status helper (pending queue count), useful for a UI badge.
export { getQueueCountAsync } from '../offline/syncStatus';
